import React from 'react';
import {
  Select,
  FormGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  TextField,
  Radio,
  RadioGroup,
  FormHelperText,
  Grid,
  MenuItem,
  InputAdornment,
  Box,
  Switch
} from '@mui/material';
import useStyles from '../useStyles';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { formatDate } from 'react-day-picker/moment';
import ErrorIcon from '@mui/icons-material/Error';
import TextEditor from '../../../NewUI/Components/CustomUIElements/TextEditor';
import { stepPropTypes } from './types';

function Step2({ answers, setAnswers, jobAdParams, requiredRefs, activeError }) {
  const classes = useStyles();

  function handleMultiCheck(value, state, key) {
    state.includes(value)
      ? setAnswers({ ...answers, [key]: state.filter((id) => id !== value) })
      : setAnswers({ ...answers, [key]: [...answers[key], value] });
  }

  return (
    <div className={classes.stepContainer}>
      <h4>Job details</h4>

      <FormControl
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium} ${classes.greyBox}`}
      >
        <Grid container alignItems="center">
          <span>Job title:</span>
          <div>{answers.jobTitle}</div>
        </Grid>
        <Grid container alignItems="center">
          <span>Job company name:</span>
          <div>{answers.companyName}</div>
        </Grid>
        <Grid container alignItems="center">
          <span>Job location:</span>
          <div>{`${[answers.address, answers.city, answers.state].filter((cur) => cur).join(', ')}`}</div>
        </Grid>
      </FormControl>

      <FormControl
        ref={requiredRefs.employmentType}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel focused={false} className={classes.label}>
          What type of employment is it?
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.employmentType && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <FormGroup>
          <Grid container>
            {jobAdParams.work_type_options.map((type) => (
              <Grid key={type[0]} item xs={6}>
                <FormControlLabel
                  disabled={
                    answers.employmentType.length >= 2 && !answers.employmentType.includes(type[1])
                  }
                  label={type[0]}
                  value={type[1]}
                  control={
                    <Checkbox
                      checked={answers.employmentType.includes(type[1])}
                      onChange={(e) =>
                        handleMultiCheck(e.target.value, answers.employmentType, 'employmentType')
                      }
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.schedule}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel focused={false} className={classes.label}>
          What is the schedule for this job?
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.schedule && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <FormGroup>
          <Grid container>
            {jobAdParams.schedules_options.map((schedule) => (
              <Grid key={schedule[1]} item xs={6}>
                <FormControlLabel
                  label={schedule[0]}
                  value={schedule[1]}
                  control={
                    <Checkbox
                      checked={answers.schedule.includes(schedule[1])}
                      onChange={(e) =>
                        handleMultiCheck(e.target.value, answers.schedule, 'schedule')
                      }
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.startDate}
        fullWidth={true}
        className={`${classes.formControl} ${classes.small}`}
      >
        <FormLabel focused={false} className={classes.label}>
          Is there a planned start date for this job?
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.startDate && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <FormGroup>
          <FormControlLabel
            label="Yes"
            control={
              <Radio
                checked={answers.isStartDate === true}
                onChange={() => setAnswers({ ...answers, isStartDate: true })}
              />
            }
          />
          <FormControlLabel
            label="No"
            control={
              <Radio
                checked={answers.isStartDate === false}
                onChange={() => setAnswers({ ...answers, isStartDate: false })}
              />
            }
          />
        </FormGroup>
        {answers.isStartDate && (
          <DayPickerInput
            value={answers.startDate}
            onDayChange={(day) => setAnswers({ ...answers, startDate: formatDate(day, 'LL') })}
            classNames={{
              container: `${classes.calendarContainer} ${activeError === requiredRefs.startDate ? classes.errorStartDate : ''}`,
              overlay: classes.calendarOverlay
            }}
            format="LL"
            placeholder="Select a date"
          />
        )}
      </FormControl>

      <h4 ref={requiredRefs.salary} className={classes.requiredHeader}>
        Salary & benefits
        <span>*</span>
        {activeError === requiredRefs.salary && (
          <div className={classes.error}>
            <ErrorIcon color="error" />
            <span>Please input valid salary range</span>
          </div>
        )}
      </h4>

      <div className={`${classes.formControl} ${classes.medium}`}>
        <Box mb={1} classes={{ root: classes.salaryInput }}>
          <Select
            classes={{ select: classes.input, root: classes.fullWidth }}
            variant="outlined"
            value={answers.selectedSalaryType}
            onChange={(e) =>
              setAnswers({
                ...answers,
                selectedSalaryType: e.target.value,
                rangeTo: 0,
                rangeFrom: 0
              })
            }
            displayEmpty={true}
            renderValue={(value) => value[0]}
          >
            {answers.salaryTypes.map((type) => (
              <MenuItem key={type[1]} value={type}>
                {type[0]}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <FormGroup>
          <Grid container classes={{ root: classes.inputAdornment }}>
            <Grid
              item
              container
              xs={9}
              alignContent="center"
              alignItems="center"
              justifyContent="center"
              wrap="nowrap"
            >
              {answers.selectedSalaryType[0] === 'Range' ? (
                <React.Fragment>
                  <Box m={1} ml={0}>
                    <TextField
                      error={activeError === requiredRefs.salary}
                      className={classes.textField}
                      variant="outlined"
                      value={Number(answers.rangeFrom).toLocaleString()}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      onChange={(e) =>
                        setAnswers({
                          ...answers,
                          rangeFrom: e.target.value
                            ? parseFloat(e.target.value.split(',').join(''))
                            : ''
                        })
                      }
                    />
                  </Box>
                  <Box sx={{ marginTop: '20px' }} m={1}>
                    <span>to</span>
                  </Box>
                  <Box m={1}>
                    <TextField
                      error={activeError === requiredRefs.salary}
                      className={classes.textField}
                      variant="outlined"
                      value={Number(answers.rangeTo).toLocaleString()}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      onChange={(e) =>
                        setAnswers({
                          ...answers,
                          rangeTo: e.target.value
                            ? parseFloat(e.target.value.split(',').join(''))
                            : ''
                        })
                      }
                    />
                  </Box>
                </React.Fragment>
              ) : (
                <TextField
                  error={activeError === requiredRefs.salary}
                  style={{ width: '100%', marginRight: 10 }}
                  className={classes.textField}
                  variant="outlined"
                  value={Number(answers.rangeTo).toLocaleString()}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  onChange={(e) =>
                    setAnswers({
                      ...answers,
                      rangeTo: e.target.value ? parseFloat(e.target.value.split(',').join('')) : '',
                      rangeFrom: e.target.value
                        ? parseFloat(e.target.value.split(',').join(''))
                        : ''
                    })
                  }
                />
              )}
            </Grid>
            <Grid item xs={3}>
              <Box m={1} mr={0}>
                <Select
                  classes={{ select: classes.input }}
                  className={classes.salaryDropdown}
                  variant="outlined"
                  value={answers.rangeUnit}
                  onChange={(e) => setAnswers({ ...answers, rangeUnit: e.target.value })}
                  displayEmpty={true}
                  renderValue={(value) => value[1]}
                >
                  {jobAdParams.payment_frequencies_options.map((range) => (
                    <MenuItem key={range[0]} value={range}>
                      {range[1]}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>
          </Grid>
        </FormGroup>
        <Box mt={1}>
          <span>Specifying $0 omits salary from your job ad</span>
        </Box>
      </div>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.medium}`}>
        <FormLabel focused={false} className={classes.label}>
          Are any forms of additional pay offered?
        </FormLabel>
        <FormGroup>
          <Grid container>
            {jobAdParams.additional_payment_options.map((pay) => (
              <Grid key={pay[1]} item xs={6}>
                <FormControlLabel
                  label={pay[0]}
                  value={pay[1]}
                  control={
                    <Checkbox
                      checked={answers.additionalPay.includes(pay[1])}
                      onChange={(e) =>
                        handleMultiCheck(e.target.value, answers.additionalPay, 'additionalPay')
                      }
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.medium}`}>
        <FormLabel focused={false} className={classes.label}>
          Are any of the following benefits offered?
        </FormLabel>
        <FormGroup>
          <Grid container>
            {jobAdParams.salary_benefit_options.map((benefit) => (
              <Grid key={benefit[1]} item xs={6}>
                <FormControlLabel
                  label={benefit[0]}
                  value={benefit[1]}
                  control={
                    <Checkbox
                      checked={answers.benefits.includes(benefit[1])}
                      onChange={(e) =>
                        handleMultiCheck(e.target.value, answers.benefits, 'benefits')
                      }
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </FormControl>

      <h4>Application Settings</h4>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.medium}`}>
        <FormLabel focused={false} className={classes.label}>
          Do you want applicants to submit a resume?
        </FormLabel>
        <RadioGroup
          value={answers.submitResume}
          onChange={(e) => setAnswers({ ...answers, submitResume: e.target.value })}
        >
          <FormControlLabel label="Yes" value={'required'} control={<Radio />} />
          <FormControlLabel label="No" value={'hidden'} control={<Radio />} />
          <FormControlLabel label="Optional" value={'optional'} control={<Radio />} />
        </RadioGroup>
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.small}`}>
        <FormLabel focused={false} className={classes.label}>
          Is there an application deadline?
        </FormLabel>
        <FormGroup>
          <FormControlLabel
            label="Yes"
            control={
              <Radio
                checked={answers.isApplicationDeadline === true}
                onChange={() => setAnswers({ ...answers, isApplicationDeadline: true })}
              />
            }
          />
          <FormControlLabel
            label="No"
            control={
              <Radio
                checked={answers.isApplicationDeadline === false}
                onChange={() => setAnswers({ ...answers, isApplicationDeadline: false })}
              />
            }
          />
        </FormGroup>
        {answers.isApplicationDeadline && (
          <DayPickerInput
            value={answers.applicationDeadline}
            onDayChange={(day) =>
              setAnswers({ ...answers, applicationDeadline: formatDate(day, 'LL') })
            }
            classNames={{ container: `${classes.calendarContainer}` }}
            format="LL"
            placeholder="Select a date"
          />
        )}
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.large}`}>
        <Grid container>
          <Grid item xs={10}>
            <FormLabel focused={false} className={classes.label}>
              Do you want to let applicants start the conversation?
            </FormLabel>
            <span className={classes.subLabel}>
              Allow job seekers to message you about their applications.
              <br />
              Messages will be sent to the email address you added in Application settings.
            </span>
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              label={answers.startConversation ? 'Yes' : 'No'}
              control={
                <Switch
                  checked={answers.startConversation}
                  onChange={() =>
                    setAnswers({ ...answers, startConversation: !answers.startConversation })
                  }
                />
              }
            />
          </Grid>
        </Grid>
      </FormControl>

      <h4 ref={requiredRefs.description} className={classes.requiredHeader}>
        Job description
        <span>*</span>
        {activeError === requiredRefs.description && (
          <div className={classes.error}>
            <ErrorIcon color="error" />
            <span>This field is required</span>
          </div>
        )}
      </h4>
      <FormControl
        fullWidth={true}
        className={`${classes.formControl} ${classes.large}`}
        style={{ marginTop: 5 }}
      >
        <FormLabel focused={false} className={classes.label}>
          Describe the responsbilities of this job, required work experience, skills, or education.
        </FormLabel>
        <TextEditor
          editorState={answers.description}
          setAnswers={setAnswers}
          answers={answers}
          activeError={activeError}
          requiredRef={requiredRefs.description}
          classes={classes}
        />
      </FormControl>

      <FormControl fullWidth={true} className={`${classes.formControl} ${classes.large}`}>
        <FormLabel className={classes.label}>
          Are you taking any additional COVID-19 precautions?
        </FormLabel>
        <span className={classes.subLabel}>
          Let job seekers know how you&apos;re responding to COVID-19
        </span>
        <TextField
          multiline={true}
          placeholder="Eg. All customers are required to wear a mask, curbside pickup available, common surfaces sanitised regularly, etc."
          className={`${classes.textField} ${classes.textBox}`}
          variant="outlined"
          value={answers.covidPrecautions}
          onChange={(e) =>
            e.target.value.length <= 255 &&
            setAnswers({ ...answers, covidPrecautions: e.target.value })
          }
        />
        <FormHelperText className={classes.helperText}>
          {answers.covidPrecautions.length}/255
        </FormHelperText>
      </FormControl>

      <FormControl
        ref={requiredRefs.jobReference}
        fullWidth={true}
        className={`${classes.formControl} ${classes.small}`}
      >
        <FormLabel required={true} focused={false} className={classes.label}>
          Job reference ID
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.jobReference}
          type="number"
          className={classes.textField}
          variant="outlined"
          value={answers.jobReference}
          onChange={(e) => setAnswers({ ...answers, jobReference: e.target.value })}
          InputProps={{
            endAdornment: activeError === requiredRefs.jobReference && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }}
        />
      </FormControl>

      <h4>Applicant qualifications</h4>

      <FormControl fullWidth={true} className={classes.large} style={{ marginTop: 14 }}>
        <Grid container>
          <Grid item xs={10}>
            <span className={classes.label}>Include applicant questionnaire from your job.</span>
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              label={answers.askQualifications ? 'On' : 'Off'}
              control={
                <Switch
                  checked={answers.askQualifications}
                  onChange={() =>
                    setAnswers({ ...answers, askQualifications: !answers.askQualifications })
                  }
                />
              }
            />
          </Grid>
        </Grid>
      </FormControl>
    </div>
  );
}

Step2.propTypes = stepPropTypes;

export default Step2;
